import { useI18n } from '@affine/i18n';
import { CloseIcon, SearchIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  forwardRef,
  type InputHTMLAttributes,
  useCallback,
  useRef,
} from 'react';

import * as styles from './settings-search-field.css';

export interface SettingsSearchFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'className' | 'placeholder'
> {
  value: string;
  onChange: (value: string) => void;
  /** Also names the field for screen readers. */
  placeholder: string;
  className?: string;
}

/**
 * The one search field of the settings: the icon in front, and a button that
 * empties the field once it holds text.
 */
export const SettingsSearchField = forwardRef<
  HTMLInputElement,
  SettingsSearchFieldProps
>(function SettingsSearchField(
  { value, onChange, placeholder, className, ...input },
  ref
) {
  const t = useI18n();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setInput = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === 'function') ref(element);
      else if (ref) ref.current = element;
    },
    [ref]
  );

  return (
    <div className={clsx(styles.field, className)}>
      <span className={styles.icon} aria-hidden="true">
        <SearchIcon />
      </span>
      <input
        aria-label={placeholder}
        {...input}
        ref={setInput}
        className={styles.input}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
      />
      {value && !input.disabled ? (
        <button
          type="button"
          className={styles.clear}
          aria-label={t['com.streamlain.settings.ui.search.clear']()}
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
        >
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
});
